import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Hand,
  ListOrdered,
  Lock,
  Pause,
  Pencil,
  Play,
  Plus,
  Send,
  Split,
  Square,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Attachment } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber, relativeTime } from '../lib/format';
import { creatableQTaskDepartments } from './QTaskForm';
import { AttachmentComposer, AttachmentList, AttachmentsCard, DropZone, useUploads } from '../components/Attachments';
import { TaskHolderName } from '../components/TaskHolder';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Tabs,
  Textarea,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * There is no department picker: the server decides what is in view. A person
 * sees their own departments' Q Tasks; `q_tasks.view_all` (the admin) sees every
 * department's, each queue under its department's name.
 */
export function QTasksPage() {
  const { t, locale } = useI18n();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('queue');

  const queuesQuery = useQuery({
    queryKey: ['q-tasks', 'queues'],
    queryFn: () => api.get<any[]>('/q-tasks/queues'),
    enabled: tab === 'queue',
    refetchInterval: 20_000,
  });

  const listQuery = useQuery({
    queryKey: ['q-tasks', 'list', tab],
    queryFn: () =>
      api.get<any>('/q-tasks', {
        ...(tab === 'available' ? { availableNow: 'true' } : {}),
        ...(tab === 'mine' ? { mine: 'true' } : {}),
        ...(tab === 'done' ? { status: 'DONE' } : {}),
        ...(tab === 'converted' ? { status: 'CONVERTED' } : {}),
        ...(tab === 'blocked' ? { status: 'BLOCKED' } : {}),
        pageSize: 50,
      }),
    enabled: tab !== 'queue',
  });

  const creatable = creatableQTaskDepartments(user, can);
  const canCreate = creatable === 'all' || creatable.length > 0;

  return (
    <>
      <PageHeader
        title={t('qtasks.title')}
        subtitle={t('qtasks.poolNote')}
        action={
          canCreate && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/q-tasks/new')}>
              {t('qtasks.new')}
            </Button>
          )
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'queue', label: t('qtasks.queue') },
          { id: 'available', label: t('qtasks.availableNow') },
          { id: 'mine', label: t('qtasks.assignedToMe') },
          { id: 'blocked', label: t('common.status') + ': ' + enumLabel('BLOCKED', locale) },
          { id: 'done', label: enumLabel('DONE', locale) },
          { id: 'converted', label: enumLabel('CONVERTED', locale) },
        ]}
      />

      <div className="mt-5">
        {tab === 'queue' ? (
          queuesQuery.isLoading ? (
            <LoadingBlock />
          ) : queuesQuery.error ? (
            <ErrorState error={queuesQuery.error} onRetry={() => void queuesQuery.refetch()} />
          ) : !queuesQuery.data?.length ? (
            <Card>
              <EmptyState
                icon={<ListOrdered className="h-8 w-8" />}
                title={t('qtasks.emptyQueue')}
                hint={t('qtasks.emptyQueueHint')}
              />
            </Card>
          ) : (
            <div className="space-y-8">
              {queuesQuery.data.map((queue) => (
                <QueueView key={queue.departmentId} queue={queue} />
              ))}
            </div>
          )
        ) : listQuery.isLoading ? (
          <LoadingBlock />
        ) : listQuery.error ? (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        ) : !listQuery.data?.items.length ? (
          <Card>
            <EmptyState icon={<ListOrdered className="h-8 w-8" />} hint={t('qtasks.emptyTabHint')} />
          </Card>
        ) : (
          <div className="space-y-2">
            {listQuery.data.items.map((q: any) => (
              <QTaskRow key={q.id} qTask={q} />
            ))}
          </div>
        )}
      </div>

    </>
  );
}

/** Nobody has it, so anyone in the department may take it. */
const isClaimable = (status: string) => status === 'AVAILABLE' || status === 'QUEUED';
/** Still to be done: waiting, with someone, or blocked. */
const isOpen = (status: string) => isClaimable(status) || ['ASSIGNED', 'IN_PROGRESS', 'BLOCKED'].includes(status);

/**
 * §37.4 — one department's queue, under its name. Since 2026-10-03 it is a pool
 * in the leader's order: anyone in the department takes any Q Task nobody has,
 * and the first of those is "next up" — a suggestion, not a gate.
 */
function QueueView({ queue }: { queue: any }) {
  const { t, locale, localized } = useI18n();
  const { user, can, isLeaderOf } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reorderMode, setReorderMode] = useState(false);
  const [order, setOrder] = useState<string[] | null>(null);

  const departmentId: string = queue.departmentId;
  const canLead = can(P.Q_TASKS_OVERRIDE) || isLeaderOf(departmentId);
  // Assigning and converting are routes that ask for the override itself.
  const canAssign = can(P.Q_TASKS_OVERRIDE);
  const canConvert = canAssign && can(P.TASKS_CREATE);
  // §21.5 — only the department's own members claim. The admin sees every
  // queue but claims only in the departments they belong to.
  const isMember = user?.departments.some((d) => d.id === departmentId) ?? false;
  const toggleReorder = () => {
    setOrder(null);
    setReorderMode((v) => !v);
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const act = useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: string; body?: unknown }) =>
      api.post(`/q-tasks/${id}/${action}`, body ?? {}),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast.error(e.message),
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => api.post('/q-tasks/reorder', { departmentId, orderedIds }),
    onSuccess: () => {
      toggleReorder();
      invalidate();
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Only the ones nobody has taken move; they swap among the places they hold,
  // the way the server does it, so the taken ones stay put on screen too.
  const waitingIds: string[] = queue.items.filter((i: any) => isClaimable(i.status)).map((i: any) => i.id);
  const workingOrder = order ?? waitingIds;

  const moveItem = (index: number, direction: -1 | 1) => {
    const next = [...workingOrder];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  };

  let slot = 0;
  const orderedItems = reorderMode
    ? queue.items.map((i: any) =>
        isClaimable(i.status) ? queue.items.find((x: any) => x.id === workingOrder[slot++]) : i,
      )
    : queue.items;

  return (
    <section aria-labelledby={`queue-${departmentId}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <h2 id={`queue-${departmentId}`} className="text-h3 text-[var(--text-primary)]">
          {localized(queue.department)}
        </h2>
        <Badge tone="neutral" className="tabular" title={`${t('common.total')}: ${queue.items.length}`}>
          <span className="sr-only">{t('common.total')}: </span>
          {queue.items.length}
        </Badge>
      </div>

      {canLead && waitingIds.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-body-sm text-muted">{t('qtasks.reorderHint')}</p>
          <div className="flex gap-2">
            {reorderMode && (
              <Button
                variant="primary"
                size="sm"
                loading={reorder.isPending}
                onClick={() => reorder.mutate(workingOrder)}
              >
                {t('common.save')}
              </Button>
            )}
            <Button size="sm" variant={reorderMode ? 'ghost' : 'secondary'} onClick={toggleReorder}>
              {reorderMode ? t('common.cancel') : t('qtasks.reorder')}
            </Button>
          </div>
        </div>
      )}

      <ol className="space-y-2">
        {orderedItems.map((item: any, index: number) => {
          // While reordering, the suggestion would follow the old order; leave it out.
          const isNext = !reorderMode && item.isNext;
          const isMine = item.assignedUserId === user?.id;
          const waitingIndex = workingOrder.indexOf(item.id);

          return (
            <li
              key={item.id}
              className={clsx(
                'surface rounded-lg p-4 transition-colors duration-fast',
                isNext && 'border-primary/50 shadow-edge-brand',
              )}
            >
              <div className="flex flex-wrap items-start gap-4">
                <span
                  className={clsx(
                    'grid h-9 w-9 shrink-0 place-items-center rounded-md font-mono text-mono font-bold tabular',
                    isNext ? 'bg-primary text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]',
                  )}
                  aria-hidden
                >
                  #{index + 1}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/q-tasks/${item.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400 hover:underline">
                      {item.number}
                    </Link>
                    <Badge tone={statusTone(item.status)}>{enumLabel(item.status, locale)}</Badge>
                    {isNext && <Badge tone="brand" dot>{t('qtasks.next')}</Badge>}
                  </div>
                  <p className="mt-1 text-title text-[var(--text-primary)]">{item.title}</p>
                  {item.description && <p className="mt-0.5 text-body-sm text-muted">{item.description}</p>}
                  {item.blockedReason && (
                    <p className="mt-1.5 rounded-sm border border-warning/30 bg-warning/8 px-2 py-1 text-caption text-warning-fg dark:text-warning-dark">
                      {item.blockedReason}
                    </p>
                  )}
                  {item.assignee && (
                    <p className="mt-1.5 text-caption text-muted">
                      {localized(item.assignee)}
                      {item.assignedAt && ` · ${formatDateTime(item.assignedAt, locale)}`}
                    </p>
                  )}
                  {isOpen(item.status) && (item.startAt || item.dueAt) && <QTaskDates qTask={item} />}
                  {item.status === 'CONVERTED' && item.tasks?.length > 0 && <TasksProgress tasks={item.tasks} />}
                </div>

                {/* A line of its own on a phone, where five buttons cannot sit beside the title. */}
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
                  {reorderMode ? (
                    isClaimable(item.status) && (
                      <>
                        <Button
                          size="sm"
                          icon={<ChevronUp className="h-4 w-4" />}
                          aria-label={t('qtasks.reorder')}
                          onClick={() => moveItem(waitingIndex, -1)}
                        />
                        <Button
                          size="sm"
                          icon={<ChevronDown className="h-4 w-4" />}
                          aria-label={t('qtasks.reorder')}
                          onClick={() => moveItem(waitingIndex, 1)}
                        />
                      </>
                    )
                  ) : (
                    <>
                      {isClaimable(item.status) && isMember && can(P.Q_TASKS_EXECUTE) && (
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Hand className="h-4 w-4" />}
                          loading={act.isPending}
                          onClick={() => act.mutate({ id: item.id, action: 'claim' })}
                        >
                          {t('qtasks.claim')}
                        </Button>
                      )}
                      {item.status === 'ASSIGNED' && isMine && (
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Play className="h-4 w-4 flip-rtl" />}
                          onClick={() => act.mutate({ id: item.id, action: 'start' })}
                        >
                          {t('qtasks.start')}
                        </Button>
                      )}
                      {item.status === 'IN_PROGRESS' && isMine && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            icon={<Square className="h-4 w-4" />}
                            onClick={() => act.mutate({ id: item.id, action: 'complete' })}
                          >
                            {t('qtasks.complete')}
                          </Button>
                          <Button
                            size="sm"
                            icon={<Pause className="h-4 w-4" />}
                            onClick={() => navigate(`/q-tasks/${item.id}/block`)}
                          >
                            {t('qtasks.block')}
                          </Button>
                        </>
                      )}
                      {item.status === 'BLOCKED' && canLead && (
                        <Button size="sm" onClick={() => act.mutate({ id: item.id, action: 'unblock' })}>
                          {t('qtasks.unblock')}
                        </Button>
                      )}
                      {canAssign && isOpen(item.status) && (
                        <Button
                          size="sm"
                          icon={<UserPlus className="h-4 w-4" />}
                          onClick={() => navigate(`/q-tasks/${item.id}/assign`)}
                        >
                          {t('qtasks.assign')}
                        </Button>
                      )}
                      {canConvert && isOpen(item.status) && (
                        <Button
                          size="sm"
                          icon={<Split className="h-4 w-4" />}
                          onClick={() => navigate(`/q-tasks/${item.id}/convert`)}
                        >
                          {t('qtasks.convert')}
                        </Button>
                      )}
                      {canLead && isOpen(item.status) && item.status !== 'IN_PROGRESS' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<X className="h-4 w-4" />}
                          onClick={() => navigate(`/q-tasks/${item.id}/cancel`)}
                        >
                          {t('common.cancel')}
                        </Button>
                      )}
                      {['ASSIGNED', 'IN_PROGRESS'].includes(item.status) && (isMine || canLead) && (
                        <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: item.id, action: 'release' })}>
                          {t('qtasks.release')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function QTaskRow({ qTask }: { qTask: any }) {
  const { t, locale, localized } = useI18n();
  return (
    <Link
      to={`/q-tasks/${qTask.id}`}
      className="surface flex flex-wrap items-center gap-3 rounded-lg p-4 transition-colors duration-fast hover:border-primary/50"
    >
      <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{qTask.number}</span>
      <span className="min-w-0 flex-1 truncate text-body text-[var(--text-primary)]">{qTask.title}</span>
      <span className="text-caption text-muted">{localized(qTask.department)}</span>
      {qTask.assignee && <span className="text-caption text-muted">{localized(qTask.assignee)}</span>}
      {isOpen(qTask.status) && qTask.dueAt && (
        <span className={clsx('tabular text-caption', isOverdue(qTask) ? 'text-error-fg dark:text-error-dark' : 'text-muted')}>
          {t('tasks.deadline')}: {formatDateTime(qTask.dueAt, locale)}
        </span>
      )}
      <Badge tone={statusTone(qTask.status)}>{enumLabel(qTask.status, locale)}</Badge>
    </Link>
  );
}

/** What an edit changed, by the edit form's labels. */
const EDITED_FIELD: Record<string, TranslationKey> = {
  title: 'common.name',
  description: 'common.description',
  priority: 'common.priority',
};

export function QTaskDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user, can, isLeaderOf } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: qTask, isLoading, error, refetch } = useQuery({
    queryKey: ['q-task', id],
    queryFn: () => api.get<any>(`/q-tasks/${id}`),
  });

  const claim = useMutation({
    mutationFn: () => api.post(`/q-tasks/${id}/claim`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['q-task', id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // Whoever added a file may take it off; so may a leader of the department.
  const canRemove = (a: Attachment) =>
    a.file.uploadedBy?.id === user?.id || can(P.Q_TASKS_OVERRIDE) || isLeaderOf(qTask.departmentId);
  const isMember = user?.departments.some((d) => d.id === qTask.departmentId) ?? false;
  const canClaim = isClaimable(qTask.status) && isMember && can(P.Q_TASKS_EXECUTE);
  const canAssign = isOpen(qTask.status) && can(P.Q_TASKS_OVERRIDE);
  const canConvert = canAssign && can(P.TASKS_CREATE);
  // The server's rule: until someone starts on it, whoever created it or a leader of its queue.
  const canEdit = !!qTask.can?.edit;
  // An edit's history line names what changed, the way the edit form labels it.
  const editedFields = (a: any): string | null => {
    const fields: string[] = a.action === 'q_task.updated' && Array.isArray(a.metadata?.fields) ? a.metadata.fields : [];
    if (fields.length === 0) return null;
    const names = fields.map((f) => (EDITED_FIELD[f] ? t(EDITED_FIELD[f]) : f));
    return t('qtasks.activity.edited', { fields: names.join(locale === 'ar' ? '، ' : ', ') });
  };
  // Someone's first opening, in the reader's language (2026-10-13); the other lines keep their action's name.
  const actionLabel = (a: any): string =>
    a.action === 'q_task.viewed' ? t('tasks.activity.viewed') : a.action.replace('q_task.', '').replace(/_/g, ' ');

  return (
    <>
      <div className="mb-4">
        <Link to="/q-tasks" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('qtasks.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{qTask.number}</span>
            <span>{qTask.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(qTask.status)}>{enumLabel(qTask.status, locale)}</Badge>
            {qTask.priority && <Badge tone={priorityTone(qTask.priority)}>{enumLabel(qTask.priority, locale)}</Badge>}
            {isOverdue(qTask) && <Badge tone="error">{t('tickets.overdue')}</Badge>}
            <span className="text-muted">{localized(qTask.department)}</span>
          </span>
        }
        action={
          (canEdit || canClaim || canAssign) && (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/q-tasks/${id}/edit`)}>
                  {t('common.edit')}
                </Button>
              )}
              {canClaim && (
                <Button
                  variant="primary"
                  icon={<Hand className="h-4 w-4" />}
                  loading={claim.isPending}
                  onClick={() => claim.mutate()}
                >
                  {t('qtasks.claim')}
                </Button>
              )}
              {canAssign && (
                <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => navigate(`/q-tasks/${id}/assign`)}>
                  {t('qtasks.assign')}
                </Button>
              )}
              {canConvert && (
                <Button icon={<Split className="h-4 w-4" />} onClick={() => navigate(`/q-tasks/${id}/convert`)}>
                  {t('qtasks.convert')}
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader title={t('common.description')} />
            <p className="whitespace-pre-wrap text-body text-[var(--text-primary)]">{qTask.description || '—'}</p>
            {qTask.blockedReason && (
              <div className="mt-4 rounded-md border border-warning/30 bg-warning/8 p-3">
                <p className="mb-1 text-caption uppercase text-warning-fg dark:text-warning-dark">{t('qtasks.block')}</p>
                <p className="text-body-sm">{qTask.blockedReason}</p>
              </div>
            )}
            {qTask.completionNote && (
              <div className="mt-4 rounded-md border border-success/30 bg-success/8 p-3">
                <p className="mb-1 text-caption uppercase text-success-fg dark:text-success">{t('qtasks.complete')}</p>
                <p className="text-body-sm">{qTask.completionNote}</p>
              </div>
            )}
          </Card>

          {qTask.tasks?.length > 0 && <MadeIntoTasks tasks={qTask.tasks} />}

          <AttachmentsCard
            attachments={qTask.attachments}
            basePath={`/q-tasks/${id}`}
            queryKey={['q-task', id]}
            canRemove={canRemove}
          />

          <QTaskReplies qTask={qTask} />
        </div>

        {/* Its own height, not the stretched height of the column beside it. */}
        <Card className="self-start">
          <CardHeader title={t('tickets.details')} />
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('qtasks.position')}>#{qTask.queuePosition}</Detail>
            <Detail label={t('common.department')}>{localized(qTask.department)}</Detail>
            <Detail label={t('common.assignee')}>
              {qTask.assignee ? localized(qTask.assignee) : t('common.unassigned')}
            </Detail>
            <Detail label={t('tasks.createdBy')}>{localized(qTask.createdBy)}</Detail>
            <Detail label={t('common.createdAt')}>{formatDateTime(qTask.createdAt, locale)}</Detail>
            {qTask.assignedAt && <Detail label={t('qtasks.claimedAt')}>{formatDateTime(qTask.assignedAt, locale)}</Detail>}
            {qTask.startAt && <Detail label={t('tasks.startDate')}>{formatDateTime(qTask.startAt, locale)}</Detail>}
            {qTask.dueAt && (
              <Detail label={t('tasks.deadline')}>
                <span className={isOverdue(qTask) ? 'text-error-fg dark:text-error-dark' : undefined}>{formatDateTime(qTask.dueAt, locale)}</span>
              </Detail>
            )}
            {qTask.startedAt && <Detail label={t('qtasks.start')}>{formatDateTime(qTask.startedAt, locale)}</Detail>}
            {qTask.completedAt && <Detail label={t('qtasks.complete')}>{formatDateTime(qTask.completedAt, locale)}</Detail>}
          </dl>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title={t('tickets.timeline')} />
          {qTask.activities?.length ? (
            <ul className="space-y-2">
              {qTask.activities.map((a: any) => {
                const edited = editedFields(a);
                return (
                  <li key={a.id} className="flex flex-wrap items-baseline gap-2 text-body-sm">
                    <span className="tabular text-caption text-faint">{formatDateTime(a.occurredAt, locale)}</span>
                    <span className="text-[var(--text-primary)]">{edited ?? actionLabel(a)}</span>
                    {a.fromStatus && a.toStatus && (
                      <span className="text-caption text-muted">
                        {enumLabel(a.fromStatus, locale)} → {enumLabel(a.toStatus, locale)}
                      </span>
                    )}
                    {a.actor && <span className="text-caption text-muted">· {localized(a.actor)}</span>}
                    {/* An edit's line already says what changed, in the reader's language. */}
                    {a.message && !edited && <span className="text-caption text-muted">— {a.message}</span>}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-body-sm text-muted">—</p>
          )}
        </Card>
      </div>
    </>
  );
}

/** Still open and past its deadline. */
const isOverdue = (qTask: { status: string; dueAt?: string | null }) =>
  isOpen(qTask.status) && !!qTask.dueAt && new Date(qTask.dueAt) < new Date();

/** The start and deadline its leader gave it, flagged once the deadline has passed. */
function QTaskDates({ qTask }: { qTask: any }) {
  const { t, locale } = useI18n();
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 text-caption text-muted">
      <span className="tabular">
        {qTask.startAt ? formatDateTime(qTask.startAt, locale) : '—'}
        {/* The arrow points the way the line reads: → in English, ← in Arabic. */}
        {` ${locale === 'ar' ? '←' : '→'} `}
        {qTask.dueAt ? formatDateTime(qTask.dueAt, locale) : '—'}
      </span>
      {isOverdue(qTask) && <Badge tone="error">{t('tickets.overdue')}</Badge>}
    </p>
  );
}

/** How far a converted Q Task's tasks have got, and whose they are: "A, B · 1 of 2 tasks done". */
function TasksProgress({ tasks }: { tasks: Array<{ status: string; assignee?: any }> }) {
  const { t, locale, localized } = useI18n();
  const done = tasks.filter((task) => task.status === 'DONE').length;
  const people = [...new Map(tasks.filter((task) => task.assignee).map((task) => [task.assignee.id, task.assignee])).values()];
  return (
    <p className="mt-1.5 text-caption text-muted">
      {people.length > 0 && `${people.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ')} · `}
      <span className="tabular">
        {t('qtasks.tasksProgress', { done: formatNumber(done, locale), total: formatNumber(tasks.length, locale) })}
      </span>
    </p>
  );
}

/** What a converted Q Task became: each task, whose it is, and when it starts and is due. */
function MadeIntoTasks({ tasks }: { tasks: any[] }) {
  const { t, locale, localized } = useI18n();
  const done = tasks.filter((task) => task.status === 'DONE').length;
  return (
    <Card>
      <CardHeader
        title={t('qtasks.madeInto')}
        action={
          <Badge tone={done === tasks.length ? 'success' : 'neutral'} className="tabular">
            {t('qtasks.tasksProgress', { done: formatNumber(done, locale), total: formatNumber(tasks.length, locale) })}
          </Badge>
        }
      />
      <p className="mb-3 text-body-sm text-muted">{t('qtasks.followsTasks')}</p>
      <ul className="divide-y divide-[var(--line)]">
        {tasks.map((task) => (
          <li key={task.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5 first:pt-0 last:pb-0">
            {/* Someone else's task stays theirs: the queue shows it, only its own people open it. */}
            {task.canOpen === false ? (
              <span className="min-w-0 flex-1 truncate text-body text-[var(--text-primary)]">{task.title}</span>
            ) : (
              <Link to={`/tasks/${task.id}`} className="min-w-0 flex-1 truncate text-body text-primary-600 dark:text-primary-400 hover:underline">
                {task.title}
              </Link>
            )}
            <span className="text-body-sm text-[var(--text-secondary)]">
              <TaskHolderName task={task} empty={t('common.unassigned')} />
            </span>
            <span className="tabular text-caption text-muted">
              {task.startAt ? formatDateTime(task.startAt, locale) : '—'}
              {/* The arrow points the way the line reads: → in English, ← in Arabic. */}
              {` ${locale === 'ar' ? '←' : '→'} `}
              {task.dueAt ? formatDateTime(task.dueAt, locale) : '—'}
            </span>
            <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * The replies on a Q Task — words, files and voice notes, from anyone in the
 * department. The person working on it and the leader who queued it are told.
 */
function QTaskReplies({ qTask }: { qTask: any }) {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const uploads = useUploads();
  const canPost = (body.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;

  const post = useMutation({
    mutationFn: () =>
      api.post(`/q-tasks/${qTask.id}/comments`, {
        body: body.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setBody('');
      uploads.clear();
      toast.success(t('qtasks.replyPosted'));
      void queryClient.invalidateQueries({ queryKey: ['q-task', qTask.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader
        title={t('qtasks.replies')}
        action={qTask.comments.length > 0 && <Badge tone="neutral" className="tabular">{qTask.comments.length}</Badge>}
      />
      {qTask.comments.length === 0 ? (
        <p className="mb-4 text-body-sm text-muted">{t('qtasks.noReplies')}</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {qTask.comments.map((c: any) => (
            <li key={c.id} className="rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
              <p className="text-caption text-muted">
                <span className="font-medium text-[var(--text-secondary)]">{localized(c.author)}</span> ·{' '}
                <span title={formatDateTime(c.createdAt, locale)}>{relativeTime(c.createdAt, locale)}</span>
              </p>
              {c.body && (
                <p className="mt-1 whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {c.body}
                </p>
              )}
              <AttachmentList attachments={c.attachments} className="mt-2" />
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canPost) post.mutate();
        }}
      >
        <DropZone uploads={uploads}>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={uploads.onPaste}
            placeholder={t('qtasks.writeReply')}
            aria-label={t('qtasks.writeReply')}
          />
          <AttachmentComposer
            uploads={uploads}
            className="mt-2"
            actions={
              <Button
                type="submit"
                variant="primary"
                size="sm"
                icon={<Send className="h-4 w-4 flip-rtl" />}
                loading={post.isPending}
                disabled={!canPost}
              >
                {t('qtasks.reply')}
              </Button>
            }
          />
        </DropZone>
      </form>
    </Card>
  );
}
